import { lazy } from 'react';
import { NewsType } from './apps/efas/news/NewsApp.types';
import { AppUrls } from './urls.tsx';

const PageNotFoundPage = lazy(() => import('./components/PageNotFoundPage.tsx'));

const AboutPage = lazy(() => import('./apps/efas/staticpages/about/AboutPage.tsx'));
const BulletinsPage = lazy(() => import('./apps/efas/staticpages/resources/BulletinsPage.tsx'));
const CollaboratePage = lazy(() => import('./apps/efas/staticpages/collaborate/CollaboratePage.tsx'));
const DataAccessPage = lazy(() => import('./apps/efas/staticpages/dataaccess/DataAccessPage.tsx'));
const DynamicAboutPage = lazy(() => import('./apps/efas/staticpages/about/DynamicAboutPage.tsx'));
const DynamicCollaboratePage = lazy(() => import('./apps/efas/staticpages/collaborate/DynamicCollaboratePage.tsx'));
const DynamicDataAccessPage = lazy(() => import('./apps/efas/staticpages/dataaccess/DynamicDataAccessPage.tsx'));
const DynamicPage = lazy(() => import('./apps/efas/staticpages/products/DynamicProductsPage.tsx'));
const EventsPage = lazy(() => import('./apps/efas/news/EventsPage.tsx'));
const MonthlySummariesPage = lazy(() => import('./apps/efas/staticpages/resources/MonthlySummariesPage.tsx'));
const NewsDetail = lazy(() => import('./apps/efas/news/NewsDetail.tsx'));
const NewsPage = lazy(() => import('./apps/efas/news/NewsPage.tsx'));
const PartnersPage = lazy(() => import('./apps/efas/staticpages/collaborate/PartnersPage.tsx'));
const ProductPage = lazy(() => import('./apps/efas/staticpages/products/ProductPage.tsx'));
const ReportsPage = lazy(() => import('./apps/efas/staticpages/resources/ReportsPage.tsx'));
const ResourcesPage = lazy(() => import('./apps/efas/staticpages/resources/ResourcesPage.tsx'));
const ScientificPapersPage = lazy(() => import('./apps/efas/staticpages/resources/ScientificPapersPage.tsx'));
const SummarySubPage = lazy(() => import('./apps/efas/staticpages/resources/SummarySubPage.tsx'));
const TrainingPage = lazy(() => import('./apps/efas/staticpages/training/TrainingPage.tsx'));
const WebinarsPage = lazy(() => import('./apps/efas/staticpages/resources/WebinarsPage.tsx'));
const WebServicesPage = lazy(() => import('./apps/efas/staticpages/dataaccess/WebServicesPage.tsx'));

export const EFAS_ROUTES = [
    {
        path: "search",
        children: [
            {
                path: ":search_slug",
                lazy: async () => {
                    const { SearchPage } = await import('./apps/efas/search/SearchPage.tsx');
                    return { Component: SearchPage }
                },
            }
        ]
    },
    {
        path: "feedback",
        lazy: async () => {
            const { ContactForm } = await import('./apps/contact/ContactForm.tsx');
            return { Component: ContactForm }
        },
    },
    {
        path: "form/feedback",
        lazy: async () => {
            const { ContactForm } = await import('./apps/contact/ContactForm.tsx');
            return { Component: ContactForm }
        },
    },
    {
        path: "privacy",
        lazy: async () => {
            const { PrivacyPage } = await import('./apps/contact/PrivacyPage.tsx');
            return { Component: PrivacyPage }
        },
    },
    {
        path: "terms-and-conditions",
        lazy: async () => {
            const { EFASTermsAndConditionsPage } = await import('./apps/contact/EFASTermsAndConditionsPage.tsx');
            return { Component: EFASTermsAndConditionsPage }
        },
    },
    {
        path: "news",
        children: [
            {
                index: true,
                element: <NewsPage />,
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}/`);
                },
            },
            {
                path: "*",
                element: <PageNotFoundPage />
            }
        ]
    },
    {
        path: "events",
        children: [
            {
                index: true,
                element: <EventsPage />,
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}/`);
                },
            }
        ]
    },
    {
        path: "about",
        element: <AboutPage />,
    },
    {
        path: "products",
        element: <ProductPage />
    },
    {
        path: AppUrls.Products.Overview.routerPath,
        element: <DynamicPage activeLink={0} />,
        loader: async () => {
            return fetch(`/api/news/overview-of-available-products/`, {});
        },
    },
    {
        path: AppUrls.Products.MeteorologicalForecasts.routerPath,
        element: <DynamicPage activeLink={1} />,
        loader: async () => {
            return fetch(`/api/news/meteorological-forecasts/`, {});
        },
    },
    {
        path: AppUrls.Products.FlashFloodIndicators.routerPath,
        element: <DynamicPage activeLink={2} />,
        loader: async () => {
            return fetch(`/api/news/flash-flood-indicators/`, {});
        },
    },
    {
        path: AppUrls.Products.MediumRangeFloodForecasts.routerPath,
        element: <DynamicPage activeLink={3} />,
        loader: async () => {
            return fetch(`/api/news/medium-range-flood-forecasts/`, {});
        },
    },
    {
        path: AppUrls.Products.SeasonalOutlook.routerPath,
        element: <DynamicPage activeLink={4} />,
        loader: async () => {
            return fetch(`/api/news/seasonal-and-sub-seasonal-hydrological-outlooks/`, {});
        },
    },
    {
        path: AppUrls.Products.FloodImpactForecasts.routerPath,
        element: <DynamicPage activeLink={5} />,
        loader: async () => {
            return fetch(`/api/news/flood-impact-forecasts/`, {});
        },
    },
    {
        path: AppUrls.Products.ModelAndForecastSkill.routerPath,
        element: <DynamicPage activeLink={6} />,
        loader: async () => {
            return fetch(`/api/news/model-and-forecast-skill/`, {});
        },
    },
    {
        path: AppUrls.Products.EfasNotifications.routerPath,
        element: <DynamicPage activeLink={7} />,
        loader: async () => {
            return fetch(`/api/news/efas-notifications/`, {});
        },
    },
    {
        path: AppUrls.Products.Monitoring.routerPath,
        element: <DynamicPage activeLink={8} />,
        loader: async () => {
            return fetch(`/api/news/monitoring/`, {});
        },
    },
    {
        path: AppUrls.Products.PreTasking.routerPath,
        element: <DynamicPage activeLink={9} />,
        loader: async () => {
            return fetch(`/api/news/pre-tasking/`, {});
        },
    },
    {
        path: "data-access",
        element: <DataAccessPage />
    },
    {
        path: "collaborate",
        element: <CollaboratePage />
    },
    {
        path: "resources",
        element: <ResourcesPage />
    },
    {
        path: "bulletins",
        children: [
            {
                index: true,
                element: <BulletinsPage />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "webinars",
        children: [
            {
                index: true,
                element: <WebinarsPage />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}/`);
                },
            }
        ]
    },
    {
        path: "reports",
        children: [
            {
                index: true,
                element: <ReportsPage />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "monthly-summaries",
        element: <MonthlySummariesPage />
    },
    {
        path: "notifications",
        children: [
            {
                index: true,
                element: <SummarySubPage newsType={NewsType.Notifications} />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "meteorology",
        children: [
            {
                index: true,
                element: <SummarySubPage newsType={NewsType.Meteorology} />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "hydrology",
        children: [
            {
                index: true,
                element: <SummarySubPage newsType={NewsType.Hydrology} />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "validation-and-skill-scores",
        children: [
            {
                index: true,
                element: <SummarySubPage newsType={NewsType.Verification} />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "annual-meetings",
        children: [
            {
                index: true,
                element: <SummarySubPage newsType={NewsType.AnnualMeetings} />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "user-feedback-and-analyses",
        children: [
            {
                index: true,
                element: <SummarySubPage newsType={NewsType.UserFeedbackAndAnalyses} />
            },
            {
                path: ":detail_slug",
                element: <NewsDetail />,
                loader: async ({ params }) => {
                    return fetch(`/api/news/${params.detail_slug}`);
                },
            }
        ]
    },
    {
        path: "scientific-papers-on-efas",
        element: <ScientificPapersPage />
    },
    {
        path: "training",
        element: <TrainingPage />
    },
    {
        path: AppUrls.About.CEMS.routerPath,
        element: <DynamicAboutPage activeLink={0} />,
        loader: async () => {
            return fetch(`/api/news/copernicus-emergency-management-service-cems/`);
        },
    },
    {
        path: AppUrls.About.EFAS.routerPath,
        element: <DynamicAboutPage activeLink={1} />,
        loader: async () => {
            return fetch(`/api/news/european-flood-awareness-system-efas/`);
        },
    },
    {
        path: AppUrls.About.FAQ.routerPath,
        element: <DynamicAboutPage activeLink={2} />,
        loader: async () => {
            return fetch(`/api/news/frequently-asked-questions-faq/`);
        },
    },
    {
        path: AppUrls.DataAccess.ConditionsAccess.routerPath,
        element: <DynamicDataAccessPage activeLink={0} />,
        loader: async () => {
            return fetch(`/api/news/efas-conditions-of-access/`);
        },
    },
    {
        path: AppUrls.DataAccess.DataDownload.routerPath,
        element: <DynamicDataAccessPage activeLink={3} />,
        loader: async () => {
            return fetch(`/api/news/data-download/`);
        },
    },
    {
        path: AppUrls.DataAccess.WebServices.routerPath,
        element: <WebServicesPage />
    },
    {
        path: "partners-list-0",
        element: <PartnersPage />
    },
    {
        path: AppUrls.Collaborate.PartnersList.routerPath,
        element: <PartnersPage />
    },
    {
        path: AppUrls.Collaborate.BecomeEFASPartner.routerPath,
        element: <DynamicCollaboratePage activeLink={0} />,
        loader: async () => {
            return fetch(`/api/news/become-efas-partner/`);
        },
    },
    {
        path: AppUrls.Collaborate.EFASAndResearch.routerPath,
        element: <DynamicCollaboratePage activeLink={1} />,
        loader: async () => {
            return fetch(`/api/news/efas-research/`);
        },
    },
    {
        path: AppUrls.Collaborate.ShareYourData.routerPath,
        element: <DynamicCollaboratePage activeLink={2} />,
        loader: async () => {
            return fetch(`/api/news/share-your-data-with-efas/`);
        },
    },
    {
        path: AppUrls.Collaborate.CEMSRapidMapping.routerPath,
        element: <DynamicCollaboratePage activeLink={3} />,
        loader: async () => {
            return fetch(`/api/news/cems-rapid-mapping/`);
        },
    },
]
